import { useGetNtfRulesQuery, useGetVesselCatalogMutation } from 'app/api/facade';
import { Button, Icon, Table } from 'app/components';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/store/store';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import styles from './NotificationsPage.module.scss';
import { AreaInfo, NotificationRule, PortCatalogData, TargetInfo } from 'ais-facade-api/api';
import { NtfRuleActionCell } from 'app/components/NtfRuleActionCell';
import { Row } from 'react-table';
import { useGetPortCatalogMutation, useLazyGetAreasQuery } from 'app/api/marine-infrastructure';
import { useAlertHook } from 'app/hooks';
import clsx from 'clsx';
import { getDisplayPhraseForNotifications, getNotificationEventType } from 'app/helpers';

export interface NotificationRuleT extends NotificationRule {
  vessel: TargetInfo;
  port: PortCatalogData;
  area: AreaInfo;
}

export const NotificationsSettings = ({
  deleteNtfRuleModalState,
  createNotificationModalState,
}: {
  deleteNtfRuleModalState: [boolean, Dispatch<SetStateAction<boolean>>];
  createNotificationModalState: [boolean, Dispatch<SetStateAction<boolean>>];
}) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const [, setShowDeleteModal] = deleteNtfRuleModalState;
  const [, setShowCreateNotificationModal] = createNotificationModalState;

  const [updatedRules, setUpdatedRules] = useState<{ updated: [] }>({
    updated: [],
  });

  const selectedRules: string[] = useAppSelector((state) => state.tableSlice.fromTable?.ntfRulesArray);

  const { data, ...ntfRulesRequest } = useGetNtfRulesQuery();

  const [getVesselInfo, { ...getVesselsInfoState }] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-vessels-mutation',
  });

  const [getPortInfo, { ...getPortsInfoState }] = useGetPortCatalogMutation({
    fixedCacheKey: 'get-ports-mutation',
  });

  const [getAreasInfo, { ...getAreasInfoState }] = useLazyGetAreasQuery();

  const isLoading =
    ntfRulesRequest.isLoading ||
    ntfRulesRequest.isFetching ||
    getVesselsInfoState.isLoading ||
    getPortsInfoState.isLoading ||
    getAreasInfoState.isLoading ||
    getAreasInfoState.isFetching;

  const ntfRules = data?.rules;

  const rulesColumns = useMemo(
    () => [
      {
        Header: 'EventType',
        accessor: 'eventType',
        disableSortBy: true,
        Cell: ({ row: { original: currentRule } }: { row: Row<NotificationRule> }) => {
          return <>{currentRule.eventType ? getNotificationEventType(currentRule) : <>&mdash;</>}</>;
        },
      },
      {
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true,

        Cell: ({ row: { original: currentRule } }: { row: Row<NotificationRuleT> }) => {
          const displayPhrase = getDisplayPhraseForNotifications(currentRule);
          return <>{displayPhrase}</>;
        },
      },
      {
        Header: <Icon name="more-icon" width="13px" height="3px" />,
        accessor: 'actionCell',
        width: 34,
        minWidth: 34,
        maxWidth: 34,
        disableSortBy: true,
        Cell: ({ row: { original: currentRule } }: { row: Row<NotificationRule> }) => (
          <div className={clsx(styles.center_icon_cell, styles.action_cell)}>
            <NtfRuleActionCell rule={currentRule} deleteNtfRuleModalState={deleteNtfRuleModalState} />
          </div>
        ),
      },
    ],
    [data?.rules],
  );

  const deleteModalToggle = () => {
    setShowDeleteModal(true);
  };

  const handleClickCreate = () => {
    setShowCreateNotificationModal(true);
  };

  useEffect(() => {
    let shouldFoundVessel = false;
    let shouldFoundPort = false;
    let shouldFoundArea = false;
    if (ntfRules && ntfRules?.length > 0) {
      let mmsis: any = [];
      let ports: string[] = [];
      let areas: string[] = [];

      ntfRules.forEach((rule) => {
        if (shouldFoundVessel === false) {
          const result = rule.actors?.some((actor) => {
            return actor.actorType === 'MMSI' && actor.actorKeys?.some((key) => key.length > 0);
          });
          shouldFoundVessel = result ? result : false;
        }

        if (shouldFoundPort === false) {
          const result = rule.actors?.some((actor) => {
            return actor.actorType === 'PORT_KEY' && actor.actorKeys?.some((key) => key.length > 0);
          });
          shouldFoundPort = result ? result : false;
        }

        if (shouldFoundArea === false) {
          const result = rule.actors?.some((actor) => {
            return actor.actorType === 'AREA_KEY' && actor.actorKeys?.some((key) => key.length > 0);
          });
          shouldFoundArea = result ? result : false;
        }
        rule.actors?.map((actor) => {
          if (actor.actorType === 'MMSI') {
            const _mmsi = actor.actorKeys?.map((key) => {
              if (key.length !== 0) {
                return key.toString();
              }
            });
            return (mmsis = _mmsi && _mmsi?.toString().length > 0 ? [...mmsis, _mmsi?.toString()] : [...mmsis]);
          }

          if (actor.actorType === 'PORT_KEY') {
            const _port = actor.actorKeys?.map((key) => {
              if (key.length !== 0) {
                return [key.toString()];
              }
            });
            return (ports = _port && _port?.toString().length > 0 ? [...ports, _port?.toString()] : [...ports]);
          }

          if (actor.actorType === 'AREA_KEY') {
            const _area = actor.actorKeys?.map((key) => {
              if (key.length !== 0) {
                return key.toString();
              }
            });
            return (areas = _area && _area?.toString().length > 0 ? [...areas, _area?.toString()] : [...areas]);
          }
        });
      });

      if (shouldFoundVessel) {
        getVesselInfo({
          filter: {
            mmsi: mmsis || undefined,
          },
          order: {
            direction: 'DESC',
            by: 'LUT',
          },
          pagination: {
            offset: 0,
            limit: 1000,
          },
        }).then((res) => {
          if ('error' in res) {
            addAlert({
              data: {
                title: 'Error',
                detail: 'Error',
                messages: [{ description: t('ErrorOperationOfService'), type: 'error' }],
              },
            });
          }
        });
      }

      if (shouldFoundPort) {
        getPortInfo({
          filter: {
            key: ports,
          },
          order: {
            direction: 'ASC',
            by: 'CITY_NAME',
          },
          pagination: {
            offset: 0,
            limit: 1000,
          },
        }).then((res) => {
          if ('error' in res) {
            addAlert({
              data: {
                title: 'Error',
                detail: 'Error',
                messages: [{ description: t('ErrorOperationOfService'), type: 'error' }],
              },
            });
          }
        });
      }
      if (shouldFoundArea) {
        getAreasInfo().then((res) => {
          if ('error' in res) {
            addAlert({
              data: {
                title: 'Error',
                detail: 'Error',
                messages: [{ description: t('ErrorOperationOfService'), type: 'error' }],
              },
            });
          }
        });
      }
    }
  }, [ntfRules]);

  useEffect(() => {
    const getUpdatedRules = () => {
      const updated = ntfRules?.map((rule: NotificationRule) => {
        let port: PortCatalogData | undefined | string = {};
        let vessel: TargetInfo | undefined | string = {};
        let area: AreaInfo | undefined | string = {};

        rule.actors?.map((actor) => {
          actor.actorKeys?.forEach((key) => {
            if (actor.actorType === 'AREA_KEY') {
              const error = getAreasInfoState.error;
              area = getAreasInfoState.data?.results?.find((area) => {
                if (key === area.code) {
                  return area;
                }
              });
              if (error) {
                area = key;
                return area;
              }
            }

            if (actor.actorType === 'MMSI') {
              vessel = getVesselsInfoState.data?.results?.find((vessel) => {
                if (key === vessel.mmsi?.toString()) {
                  return vessel;
                }
              });
              if (!vessel) {
                vessel = key;
                return vessel;
              }
            }

            if (actor.actorType === 'PORT_KEY') {
              port = getPortsInfoState.data?.results?.find((port) => {
                if (key === port.key) {
                  return [port];
                }
              });
              if (!port) {
                port = key;
                return [port];
              }
            }

            return {
              port: port,
              vessel: vessel,
              area: area,
            };
          });
        });
        return { ...rule, vessel: vessel, port: port, area: area };
      });

      setUpdatedRules((state: any) => ({ ...state, updated }));
    };

    getUpdatedRules();
  }, [getVesselsInfoState.isSuccess, getPortsInfoState.isSuccess, getAreasInfoState.isSuccess]);

  return (
    <>
      <div className={styles.actions}>
        <Button label={t('ButtonLabelCreate')} variant="general" onClick={handleClickCreate} />
        <Button
          label={t('ButtonLabelDelete')}
          variant="general"
          onClick={deleteModalToggle}
          disabled={!selectedRules || selectedRules.length === 0}
        />
      </div>
      {(!updatedRules || isLoading) && (
        <div className={styles.notifications_rules_loader}>
          <Icon name="spinner-light" size="16" color={`var(--default-primary)`} className={styles.spinner_animate} />
          {t('DataIsLoading')}
        </div>
      )}
      {ntfRules?.length === 0 && <div className={styles.notifications_rules_empty}>{t('NoNotificationRules')}</div>}

      {!isLoading && ntfRules && ntfRules.length > 0 && updatedRules.updated && updatedRules.updated.length > 0 && (
        <div className={styles.table_container}>
          <Table withCheckbox manualSort={false} columns={rulesColumns} data={updatedRules.updated} />
        </div>
      )}
    </>
  );
};
